import React, {
	type ComponentType,
	type ErrorInfo,
	type ReactNode,
	type Ref,
	type RefObject,
	useCallback,
	useContext,
	useMemo,
	useRef,
} from 'react';

import { di } from 'react-magnetic-di';
import { loadEntryPoint, type EntryPointProps as RelayEntryPointProps } from 'react-relay';
import { useMergeRefs } from 'use-callback-ref';

import Modal from '@atlaskit/modal-dialog/modal-dialog';
import type { KeyboardOrMouseEvent, ModalDialogProps } from '@atlaskit/modal-dialog/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ModalErrorFallbackProps } from '@atlassian/entry-point-config/types';
import { useEntryPointConfig } from '@atlassian/entry-point-config/use-entry-point-config';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
} from '@atlassian/entry-point-types/types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container/ui';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { ModalContext } from '../modal-context-provider/modal-context';
import { useContentReadySignal } from '../use-content-ready-signal';
import { ErrorFallback } from './error-fallback';
import { FocusInsideOnLoad } from './focus-inside-on-load';
import { Loading } from './loading';
import { type ModalComponentProps, type SourceMetadata } from './types';

export { type ModalComponentProps, type SourceMetadata };

export type ModalEntryPointProps<
	TPreloadedQueries,
	TNestedEntryPoints,
	TRuntimeProps,
	TExtraProps,
> = RelayEntryPointProps<
	TPreloadedQueries,
	TNestedEntryPoints,
	TRuntimeProps & ModalComponentProps,
	TExtraProps
>;

type EntryPointProps<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

type ModalCloseRequest = {
	event?: KeyboardOrMouseEvent;
	entryPointCloseArgument?: unknown;
};

export type ModalTriggerProps<TEntryPoint> = {
	/**
	 * Renders the control that opens the modal.
	 */
	children: ({ ref }: { ref: RefObject<HTMLElement>; 'aria-haspopup'?: 'dialog' }) => ReactNode;
	/**
	 * Entry point to load when the modal opens.
	 */
	entryPoint: TEntryPoint;
	/**
	 * Parameters passed when loading the entry point.
	 */
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	/**
	 * Runtime props passed to the loaded entry point. Modal-controlled props from
	 * `ModalComponentProps` are supplied by the trigger; callers can override `onClose`.
	 */
	entryPointProps?: Omit<EntryPointProps<TEntryPoint>, keyof ModalComponentProps> & {
		onClose?: (extraInformation?: unknown) => void;
	};
	/**
	 * Renders while the entry point is loading. When omitted, the built-in accessible loading UI is
	 * used; use `loadingText` to provide localized loading text before replacing it.
	 */
	Fallback?: ComponentType<{ onClose?: () => void }>;
	/**
	 * Renders when the entry point fails to load or render. When omitted, the configured accessible
	 * error fallback is used; use `title`, `errorTitle`, `errorText`, and `errorCloseText` to
	 * provide localized default content before replacing it.
	 */
	errorFallback?: ComponentType<ModalErrorFallbackProps>;
	/**
	 * Ref attached to the trigger element.
	 */
	forwardedRef?: Ref<HTMLElement>;
	/**
	 * Additional props passed to the modal dialog.
	 */
	modalProps?: ModalDialogProps;
	/**
	 * Called when the entry point fails to load or render.
	 */
	onError?: ErrorCallback;
	/**
	 * Called when the modal requests to close. Return `false` to keep the modal open.
	 * Internal close requests have no event; their payload still reaches entryPointProps.onClose.
	 *
	 * The event matches ModalDialog's onClose event. When the top-layer implementation is enabled,
	 * it is synthetic: Escape is a keydown event and an overlay click is a click event. Do not rely
	 * on event targets or other DOM-specific event details.
	 */
	onRequestClose?: (event?: KeyboardOrMouseEvent) => boolean | void;
	/**
	 * Metadata used to identify the entry point for reporting.
	 */
	source?: SourceMetadata;
	/**
	 * Preloads the entry point when the trigger receives focus.
	 */
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	preloadOnFocus?: boolean;
	/**
	 * The title that renders during the default loading screen and the default error screen when
	 * `errorTitle` is not provided.
	 */
	title?: ReactNode;
	/**
	 * The text that renders during the loading screen.
	 */
	loadingText?: ReactNode;
	/**
	 * The title that renders during the default error screen. Falls back to `title` when omitted.
	 */
	errorTitle?: ReactNode;
	/**
	 * The text that renders during the default error screen.
	 */
	errorText?: ReactNode;
	/**
	 * The text used by the close button on the default error screen.
	 */
	errorCloseText?: string;
};

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as EntryPointProps<AnyEntryPoint>;

export function ModalTrigger<TEntryPoint extends AnyEntryPoint>({
	children,
	entryPoint,
	entryPointParams,
	entryPointProps,
	Fallback,
	errorFallback: ErrorFallbackComponent,
	title,
	modalProps,
	forwardedRef,
	onError,
	onRequestClose,
	source,
	preloadOnFocus = true,
	loadingText,
	errorTitle,
	errorText,
	errorCloseText,
}: ModalTriggerProps<TEntryPoint>): React.ReactNode {
	di(loadEntryPoint);
	const { openModal } = useContext(ModalContext);
	const environmentProvider = useRelayEnvironmentProvider();
	const { GlobalModalErrorFallback } = useEntryPointConfig();
	const ModalErrorFallback = ErrorFallbackComponent ?? GlobalModalErrorFallback;
	const modalRef = useRef<HTMLElement>(null);
	const { onContentReady, resetContentReady, waitForContentReady } = useContentReadySignal();
	const { id, packageName, teamName } = source ?? {};

	const metricName = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<unknown>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onLoad = useCallback(
		({
			reference: entryPointReference,
			dispose,
		}: {
			dispose: () => void;
			reference: EntryPointRef<TEntryPoint>;
		}) => {
			openModal(
				({
					onClose: closeModal,
					shouldCloseOnOverlayClick,
					shouldCloseOnEscapePress,
					setShouldCloseOnOverlayClick,
					setShouldCloseOnEscapePress,
				}) => {
					resetContentReady();
					const requestClose = (request: ModalCloseRequest) => {
						if (onRequestClose?.(request.event) === false) {
							return;
						}
						entryPointProps?.onClose?.(request.entryPointCloseArgument);
						closeModal();
						dispose();
					};
					const onInternalClose = (extraInformation?: unknown) => {
						requestClose({ entryPointCloseArgument: extraInformation });
					};
					const onUserInteractionClose = (event: KeyboardOrMouseEvent) => {
						requestClose({ event, entryPointCloseArgument: event });
					};

					const container = (
						<InternalEntryPointContainer
							entryPointReference={entryPointReference}
							errorFallback={({ error }: { error: Error }) => (
								<ModalErrorFallback
									error={error}
									id={id ?? metricName}
									teamName={teamName}
									packageName={packageName}
									closeModal={onInternalClose}
									fallback={
										<ErrorFallback
											error={error}
											closeModal={onInternalClose}
											errorTitle={errorTitle}
											title={title}
											errorText={errorText}
											errorCloseText={errorCloseText}
										/>
									}
								/>
							)}
							onError={({ error, errorInfo }: { error: Error; errorInfo: ErrorInfo }) => {
								onError?.({ error, errorInfo });
							}}
							fallback={
								Fallback ? (
									<Fallback onClose={onInternalClose} />
								) : (
									<Loading title={title} loadingText={loadingText} />
								)
							}
							id={metricName}
							onContentReady={onContentReady}
							placeholderName="modal-entry-point-pressable-trigger-container"
							runtimeProps={
								{
									...(entryPointProps ?? emptyEntryPointProps),
									onClose: onInternalClose,
									setShouldCloseOnOverlayClick,
									setShouldCloseOnEscapePress,
									shouldCloseOnOverlayClick,
									shouldCloseOnEscapePress,
								} as unknown as EntryPointProps<TEntryPoint>
							}
						/>
					);

					return (
						// eslint-disable-next-line @atlaskit/design-system/use-modal-dialog-close-button
						<Modal
							{...modalProps}
							UNSAFE__disableInitialAutoFocus={fg('platform_dst-automated_focus')}
							ref={modalRef}
							onClose={onUserInteractionClose}
							shouldCloseOnOverlayClick={
								shouldCloseOnOverlayClick ?? modalProps?.shouldCloseOnOverlayClick
							}
							shouldCloseOnEscapePress={
								shouldCloseOnEscapePress ?? modalProps?.shouldCloseOnEscapePress
							}
						>
							{container}
							<FocusInsideOnLoad
								entryPointReference={entryPointReference}
								autoFocusRef={modalProps?.autoFocus}
								modalRef={modalRef}
								waitForContentReady={waitForContentReady}
							/>
						</Modal>
					);
				},
			);
		},
		[
			openModal,
			title,
			modalProps,
			Fallback,
			metricName,
			id,
			packageName,
			teamName,
			entryPointProps,
			ModalErrorFallback,
			onError,
			onRequestClose,
			loadingText,
			errorTitle,
			errorText,
			errorCloseText,
			onContentReady,
			resetContentReady,
			waitForContentReady,
		],
	);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
		preloadOnFocus,
	});

	const mergedRef = useMergeRefs([triggerRef, forwardedRef || null]);

	return children({
		ref: mergedRef as unknown as RefObject<HTMLElement>,
		'aria-haspopup': 'dialog',
	});
}
